import { Alert, Card, Row, Col, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { AccessLogStatistics } from '../../services/accessLog.service';
import { format } from 'date-fns';
import { formatUserName } from '../../utils/name.util';
import { getDateFnsLocale } from '../../utils/dateFnsLocale';
import { formatDurationMs } from '../../utils/formatDuration';
import { labelForAppSurface } from '../../utils/accessLogAppLabels';
import styles from './AccessLogStatistics.module.scss';

// Chart.jsの登録
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface AccessLogStatisticsProps {
  statistics: AccessLogStatistics;
}

export default function AccessLogStatisticsComponent({ statistics }: AccessLogStatisticsProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  // 時系列データ（Line Chart）
  const timeSeriesData = {
    labels: statistics.timeSeries.map((item) => {
      try {
        return format(new Date(item.date), 'MM/dd', { locale: dateFnsLocale });
      } catch {
        return item.date;
      }
    }),
    datasets: [
      {
        label: t('access_logs.statistics.access_count', { defaultValue: 'アクセス数' }),
        data: statistics.timeSeries.map((item) => item.count),
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#667eea',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
    ],
  };

  const timeSeriesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: t('access_logs.statistics.time_series_title', {
          defaultValue: 'アクセス数推移',
        }),
        font: {
          size: 16,
          weight: 600,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          maxTicksLimit: 31,
          font: { size: 10 },
        },
      },
      y: {
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 15,
        },
      },
    },
  };

  // アクション別集計（API 監査を除く — page_view 等の意味ある操作）
  const actionsNonApi = statistics.byAction.filter((item) => item.action !== 'api');
  const actionData = {
    labels: actionsNonApi.map((item) => item.action),
    datasets: [
      {
        label: t('access_logs.statistics.count', { defaultValue: '件数' }),
        data: actionsNonApi.map((item) => item.count),
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(118, 75, 162, 0.8)',
          'rgba(237, 100, 166, 0.8)',
          'rgba(255, 154, 0, 0.8)',
          'rgba(52, 211, 153, 0.8)',
        ],
        borderColor: [
          'rgba(102, 126, 234, 1)',
          'rgba(118, 75, 162, 1)',
          'rgba(237, 100, 166, 1)',
          'rgba(255, 154, 0, 1)',
          'rgba(52, 211, 153, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const actionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: t('access_logs.statistics.action_title', {
          defaultValue: 'アクション別集計（API除く）',
        }),
        font: {
          size: 16,
          weight: 600,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 15,
        },
      },
    },
  };

  const pageViewRows = statistics.byPageView ?? [];
  const appRows = statistics.byApp ?? [];
  const environmentRows = statistics.byEnvironment ?? [];
  const environmentOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t('access_logs.statistics.environment_title', {
          defaultValue: '利用環境（ブラウザ/OS）',
        }),
        font: { size: 16, weight: 600 },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(229, 231, 235, 0.5)' },
        beginAtZero: true,
        ticks: { precision: 0, maxTicksLimit: 12 },
      },
      y: {
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 11 } },
      },
    },
  };

  const environmentData = {
    labels: environmentRows.map((item) => item.environment),
    datasets: [
      {
        label: t('access_logs.statistics.count', { defaultValue: '件数' }),
        data: environmentRows.map((item) => item.count),
        backgroundColor: 'rgba(52, 211, 153, 0.8)',
        borderColor: 'rgba(52, 211, 153, 1)',
        borderWidth: 2,
      },
    ],
  };

  const appViewData = {
    labels: appRows.map((item) => labelForAppSurface(item.appSurface, t)),
    datasets: [
      {
        label: t('access_logs.statistics.view_count', { defaultValue: '閲覧回数' }),
        data: appRows.map((item) => item.viewCount),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
      },
    ],
  };

  const appViewOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t('access_logs.statistics.app_views_title', { defaultValue: 'アプリ別閲覧回数' }),
        font: { size: 16, weight: 600 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 60,
          minRotation: 45,
          font: { size: 10 },
        },
      },
      y: {
        grid: { color: 'rgba(229, 231, 235, 0.5)' },
        beginAtZero: true,
        ticks: { precision: 0, maxTicksLimit: 15 },
      },
    },
  };

  const pageViewData = {
    labels: pageViewRows.map((item) => item.path),
    datasets: [
      {
        label: t('access_logs.statistics.count', { defaultValue: '件数' }),
        data: pageViewRows.map((item) => item.count),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
      },
    ],
  };

  const pageViewOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t('access_logs.statistics.page_view_title', {
          defaultValue: 'ページ閲覧（SPA・上位）',
        }),
        font: { size: 16, weight: 600 },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(229, 231, 235, 0.5)' },
        beginAtZero: true,
        ticks: { precision: 0, maxTicksLimit: 12 },
      },
      y: {
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 11 } },
      },
    },
  };

  const countryRows = statistics.byCountry ?? [];
  const countryData = {
    labels: countryRows.map((item) => item.countryCode),
    datasets: [
      {
        label: t('access_logs.statistics.count', { defaultValue: '件数' }),
        data: countryRows.map((item) => item.count),
        backgroundColor: 'rgba(118, 75, 162, 0.8)',
        borderColor: 'rgba(118, 75, 162, 1)',
        borderWidth: 2,
      },
    ],
  };

  const countryOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t('access_logs.statistics.country_title', {
          defaultValue: '国別（記録あり・上位）',
        }),
        font: { size: 16, weight: 600 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        grid: { color: 'rgba(229, 231, 235, 0.5)' },
        beginAtZero: true,
        ticks: { stepSize: 1, maxTicksLimit: 12 },
      },
    },
  };

  // パブリックアクセス別集計（Doughnut Chart）
  const publicAccessData = {
    labels: statistics.byPublicAccess.map((item) =>
      item.isPublicAccess
        ? t('access_logs.table.public', { defaultValue: '公開' })
        : t('access_logs.table.private', { defaultValue: '非公開' })
    ),
    datasets: [
      {
        data: statistics.byPublicAccess.map((item) => item.count),
        backgroundColor: ['rgba(102, 126, 234, 0.8)', 'rgba(156, 163, 175, 0.8)'],
        borderColor: ['rgba(102, 126, 234, 1)', 'rgba(156, 163, 175, 1)'],
        borderWidth: 2,
      },
    ],
  };

  const publicAccessOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: t('access_logs.statistics.public_access_title', {
          defaultValue: 'アクセスタイプ別集計',
        }),
        font: {
          size: 16,
          weight: 600,
        },
      },
    },
  };

  // ユーザー別集計（Bar Chart）
  const userData = {
    labels: statistics.byUser.map((item) => {
      if (item.userId === null) {
        return item.usernameSnapshot
          ? `${item.usernameSnapshot} †`
          : t('access_logs.table.anonymous', { defaultValue: '匿名' });
      }
      return formatUserName(item.firstName, item.lastName, i18n.language) || item.username || item.usernameSnapshot || '-';
    }),
    datasets: [
      {
        label: t('access_logs.statistics.count', { defaultValue: '件数' }),
        data: statistics.byUser.map((item) => item.count),
        backgroundColor: 'rgba(52, 211, 153, 0.8)',
        borderColor: 'rgba(52, 211, 153, 1)',
        borderWidth: 2,
      },
    ],
  };

  const userOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: t('access_logs.statistics.user_title', {
          defaultValue: 'ユーザー別集計（上位10名）',
        }),
        font: {
          size: 16,
          weight: 600,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 15,
        },
      },
    },
  };

  // データが空の場合の処理
  if (!statistics.timeSeries || statistics.timeSeries.length === 0) {
    return (
      <div className="mb-3">
        <h3 className={styles.sectionTitle}>
          {t('access_logs.statistics.title', { defaultValue: '統計情報' })}
        </h3>
        <Alert variant="info">
          {t('access_logs.statistics.no_data', { defaultValue: '表示するデータがありません。' })}
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <h3 className={styles.sectionTitle}>
        {t('access_logs.statistics.title', { defaultValue: '統計情報' })}
      </h3>

      {/* サマリーカード */}
      <Row className={styles.summaryRow}>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-muted small mb-2">
                {t('access_logs.statistics.total_access', { defaultValue: '総アクセス数' })}
              </Card.Title>
              <h2 className="mb-0">{statistics.total.toLocaleString()}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-muted small mb-2">
                {t('access_logs.statistics.action_types', { defaultValue: 'アクション種別数' })}
              </Card.Title>
              <h2 className="mb-0">{statistics.byAction.length}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-muted small mb-2">
                {t('access_logs.statistics.active_users', { defaultValue: 'アクティブユーザー数' })}
              </Card.Title>
              <h2 className="mb-0">
                {statistics.byUser.filter((u) => u.userId !== null).length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="text-muted small mb-2">
                {t('access_logs.statistics.public_access_count', {
                  defaultValue: 'パブリックアクセス数',
                })}
              </Card.Title>
              <h2 className="mb-0">
                {statistics.byPublicAccess.find((item) => item.isPublicAccess)?.count || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* グラフ */}
      <Row>
        <Col lg={8} className={styles.chartCol}>
          <Card>
            <Card.Body>
              <div className={styles.chartSlot}>
                <Line data={timeSeriesData} options={timeSeriesOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} className={styles.chartCol}>
          <Card>
            <Card.Body>
              <div className={styles.chartSlot}>
                <Doughnut data={publicAccessData} options={publicAccessOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {appRows.length > 0 && (
        <Row className="mb-2">
          <Col>
            <Card>
              <Card.Body>
                <h4 className="h6 mb-3">
                  {t('access_logs.statistics.app_usage_title', {
                    defaultValue: 'アプリ別利用状況（閲覧回数・滞在時間）',
                  })}
                </h4>
                <div className="table-responsive">
                  <Table bordered size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>{t('access_logs.statistics.app_column', { defaultValue: 'アプリ' })}</th>
                        <th className="text-end">
                          {t('access_logs.statistics.view_count', { defaultValue: '閲覧回数' })}
                        </th>
                        <th className="text-end">
                          {t('access_logs.statistics.total_time', { defaultValue: '合計滞在' })}
                        </th>
                        <th className="text-end">
                          {t('access_logs.statistics.avg_time', { defaultValue: '平均滞在' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {appRows.map((row) => (
                        <tr key={row.appSurface}>
                          <td>{labelForAppSurface(row.appSurface, t)}</td>
                          <td className="text-end">{row.viewCount.toLocaleString()}</td>
                          <td className="text-end">{formatDurationMs(row.totalDurationMs)}</td>
                          <td className="text-end">
                            {row.avgDurationMs > 0 ? formatDurationMs(row.avgDurationMs) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {(appRows.length > 0 || environmentRows.length > 0 || countryRows.length > 0) && (
        <Row>
          {appRows.length > 0 && (
            <Col lg={4} className={styles.chartCol}>
              <Card>
                <Card.Body>
                  <div className={styles.chartSlotTall}>
                    <Bar data={appViewData} options={appViewOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}
          {environmentRows.length > 0 && (
            <Col lg={4} className={styles.chartCol}>
              <Card>
                <Card.Body>
                  <div className={styles.chartSlot}>
                    <Bar data={environmentData} options={environmentOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}
          <Col lg={4} className={styles.chartCol}>
            <Card>
              <Card.Body>
                <div className={styles.chartSlot}>
                  {countryRows.length > 0 ? (
                    <Bar data={countryData} options={countryOptions} />
                  ) : (
                    <Alert variant="light" className="small mb-0">
                      {t('access_logs.statistics.country_empty', {
                        defaultValue:
                          '国コードの記録がありません。CDN ヘッダまたは IP 推定が有効になると表示されます。',
                      })}
                    </Alert>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {pageViewRows.length > 0 && (
        <Row>
          <Col className={styles.chartCol}>
            <Card>
              <Card.Body>
                <div className={styles.chartSlot}>
                  <Bar data={pageViewData} options={pageViewOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={6} className={styles.chartCol}>
          <Card>
            <Card.Body>
              <div className={styles.chartSlot}>
                <Bar data={actionData} options={actionOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className={styles.chartCol}>
          <Card>
            <Card.Body>
              <div className={styles.chartSlot}>
                <Bar data={userData} options={userOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

